// Helpers y constantes del Cotizador (Iteración 5) — réplica del «Creador de Itinerarios».
export { fmtEUR, fmtUSD, fmtPct, PARTNER_LABELS } from "@/pages/motor/shared";
export { fmtDate } from "@/pages/trips/shared";

export const COT_STATUS_LABELS = {
  borrador: "Borrador",
  compartido: "Compartido",
  vendido: "Vendido",
  archivado: "Archivado — No vendido",
};
// Alias de status (inglés / claves legacy anteriores al re-naming de feb 2026) → clave canónica.
// Se aplica en display y también en el filtro para que "Compartido" englobe también "enviado_cliente"/"sent".
export const COT_STATUS_ALIAS = {
  draft: "borrador",
  sent: "compartido",
  sent_client: "compartido",
  enviado_cliente: "compartido",
  won: "vendido",
  ganado: "vendido",
  lost: "archivado",
  perdido: "archivado",
  archived: "archivado",
};
export const normalizeCotStatus = (s) => COT_STATUS_ALIAS[s] || s || "borrador";
// Grupos macro del ciclo de vida:
//   activos     = cotizaciones "vivas" (borrador + compartido) — vista por defecto para los agentes.
//   no_activos  = histórico (vendido + archivado) — se consulta a demanda.
export const COT_STATUS_GROUPS = {
  activos: { label: "Activos (Borrador + Compartido)", members: ["borrador", "compartido"] },
  no_activos: { label: "No activos (Vendido + Archivado)", members: ["vendido", "archivado"] },
};
export const COT_STATUS_VARIANT = {
  borrador: "secondary",
  compartido: "default",
  vendido: "default",
  archivado: "outline",
};

// Taxonomía unificada de tipos de servicio (legacy Creador de Itinerarios + Free Day)
export {
  SERVICE_TYPE_LABELS as COT_SERVICE_TYPE_LABELS,
  mapProductType, serviceTypeToProductType, isLodgingType, isFreeType,
} from "@/lib/serviceTypes";

export const COT_OVERRIDE_LABELS = {
  no: "Estándar (markup)",
  kimkim: "KimKim (+8%)",
  override_hotel: "Override hotel",
  override: "Override (precio manual)",
  "8%": "+8%",
};

export const ROOM_TYPES = ["single", "doble", "twin", "triple", "cuadruple", "suite", "family", "otro"];
export const ROOM_PAX_DEFAULT = { single: 1, doble: 2, twin: 2, triple: 3, cuadruple: 4, suite: 2, family: 4, otro: 2 };
// Régimen de estancia (board). Labels en inglés para consistencia con el
// Travel Document (que se envía al viajero). Valores en español (sa/bb/mp/pc)
// para no romper el mapping con la BD.
export const BOARD_TYPES = [
  { value: "sa", label: "Room Only" },
  { value: "bb", label: "Bed & Breakfast" },
  { value: "mp", label: "Half Board" },
  { value: "pc", label: "Full Board" },
];

// mapProductType / serviceTypeToProductType: ver @/lib/serviceTypes (re-exportados arriba).

export const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

// ---- Fechas ----
export const daysBetween = (a, b) => {
  if (!a || !b) return 0;
  return Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000) + 1);
};
export const nightsBetween = (df, dt) => {
  if (!df || !dt) return 0;
  const a = new Date(df), b = new Date(dt);
  if (isNaN(a) || isNaN(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86400000));
};
export const dateAdd = (start, n) => {
  if (!start) return null;
  const d = new Date(start);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
export const fmtDayChip = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }); }
  catch { return d; }
};

// Rango de fechas para el listado (formato "18 abr – 27 abr" o "—" si falta alguna)
export const fmtRange = (a, b) => {
  if (!a && !b) return "—";
  return `${fmtDayChip(a)} – ${fmtDayChip(b)}`;
};

// Rango largo (con año y flecha), como en el Creador legacy: "21 oct 2026 → 28 oct 2026".
export const fmtDayChipLong = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
};
export const fmtRangeLong = (a, b) => {
  if (!a && !b) return "—";
  return `${fmtDayChipLong(a)} → ${fmtDayChipLong(b)}`;
};

// ---- Pricing helpers (mismo criterio que el backend _eff_qty / _build_items) ----
export const effQty = (s) => {
  if (s.type === "alojamiento") {
    return Math.max(1, parseInt(s.nights || 1, 10)) * Math.max(1, parseInt(s.rooms || 1, 10));
  }
  return Number(s.qty) || 1;
};

// Total de una estancia: si hay habitaciones, total = noches × Σ(coste/hab/noche).
export const accommodationTotals = (a) => {
  const nights = nightsBetween(a.date_from, a.date_to) || 0;
  const rooms = a.rooms || [];
  if (rooms.length === 0) {
    return { nights, numRooms: 0, incl: Number(a.cost_tax_incl) || 0, excl: Number(a.cost_tax_excl) || 0 };
  }
  const sumIncl = rooms.reduce((s, r) => s + (Number(r.cost_incl) || Number(r.cost_excl) || 0), 0);
  const sumExcl = rooms.reduce((s, r) => s + (Number(r.cost_excl) || Number(r.cost_incl) || 0), 0);
  const n = Math.max(1, nights);
  return {
    nights, numRooms: rooms.length,
    incl: Math.round(sumIncl * n * 100) / 100,
    excl: Math.round(sumExcl * n * 100) / 100,
  };
};

export const buildCalcItems = (doc) => {
  const items = (doc?.days || []).flatMap((d) =>
    (d.services || []).map((s) => ({
      description: s.name || "",
      country: s.country || "",
      cost_tax_excl: Number(s.cost_tax_excl) || 0,
      cost_tax_incl: Number(s.cost_tax_incl) || 0,
      qty: effQty(s),
      override: s.override || "no",
      price: Number(s.price) || 0,
    }))
  );
  (doc?.accommodations || []).forEach((a) => {
    const t = accommodationTotals(a);
    if (t.incl > 0 || t.excl > 0) {
      items.push({
        description: a.name || "Alojamiento",
        country: a.country || "",
        cost_tax_excl: t.excl,
        cost_tax_incl: t.incl,
        qty: 1,
        override: "no",
        price: 0,
      });
    }
  });
  return items;
};

export const newService = (preset = {}) => ({
  id: uid(),
  type: "actividad",
  name: "", provider_name: "", provider_id: null, product_id: null,
  city: "", country: "",
  qty: 1, pax: 1, nights: 1, rooms: 1,
  cost_tax_excl: 0, cost_tax_incl: 0,
  override: "no", price: 0, currency: "EUR", acc_id: null, notes: "",
  plantilla_id: null, sales_description: "", sales_images: [],
  ...preset,
});

export const newDay = (dayNumber, date = null) => ({
  id: uid(), day_number: dayNumber, date, label: "", city: "", services: [], notes: [],
});

// ---- It.6 E3 — Notas / sugerencias por día (estilo Travefy) ----
export const NOTE_ICONS = [
  { value: "suggestion", label: "Sugerencia" },
  { value: "city_info", label: "Info de ciudad" },
  { value: "restaurant", label: "Restaurantes" },
  { value: "driving", label: "Conducción / ruta" },
  { value: "tip", label: "Consejo de viaje" },
  { value: "warning", label: "Aviso" },
  { value: "info", label: "Información" },
];
// Punto 7: las notas nuevas nacen «Para el cliente»; las legacy sin marca se tratan como internas.
export const newNote = (preset = {}) => ({ id: uid(), icon: "suggestion", title: "", text: "", images: [], visibility: "client", ...preset });
export const noteIsInternal = (n) => (n?.visibility || "internal") !== "client";

// Punto 8: modalidad de la actividad (deducida del catálogo KimKim, editable).
export const MODALITY_LABELS = { private: "Privado", small_group: "Small group", self_guided: "Self-guided" };

// ---- It.6 E3 — Information & Documents (al final del itinerario) ----
export const newInfoSection = (preset = {}) => ({ id: uid(), title: "", body: "", ...preset });
export const DEFAULT_INFO_SECTIONS = [
  { title: "Quote Terms", body: "• This quote is valid for 7 days; after this date, the price is subject to change based on availability.\n• Based on the number of travellers and room occupancy indicated.\n• A deposit of 30% is required at the time of booking (full payment if travelling within 60 days).\n• Final payment is due 60 days before departure.\n• Final travel documents are sent electronically 2 weeks before departure.\n• No service or accommodation is reserved until the deposit is made." },
  { title: "What's Included", body: "• Accommodation as listed in the itinerary\n• Activities and experiences as listed (unless marked optional)\n• Transportation as listed in the itinerary\n• 24/7 in-trip traveller support" },
  { title: "What's Not Included", body: "• International flights to/from the destination\n• Meals not listed in the itinerary\n• Optional activities\n• Travel insurance (highly recommended)\n• Gratuities and items of a personal nature\n• Tourist city tax (paid directly at the hotel)" },
  { title: "Cancellation Policy", body: "If you need to cancel, please notify us in writing. Cancellation is effective upon receipt of written notice. Cancellation terms vary by supplier; your travel advisor will make every effort to recover any applicable costs. By booking this trip you accept our terms and conditions." },
];

export const newRoom = (preset = {}) => ({
  id: uid(), room_type: "doble", pax: 2, cost_excl: 0, cost_incl: 0, currency: "EUR", ...preset,
});

export const newAccommodation = (itn, rooms = []) => ({
  id: uid(), name: "", provider_id: null, product_id: null, city: "", country: "",
  date_from: itn?.start_date || null, date_to: itn?.end_date || null,
  rooms, cost_tax_excl: 0, cost_tax_incl: 0, currency: "EUR", board: "bb", notes: "",
  price_mode: "per_stay",  // Ventas (oct-2026): los hoteles entran siempre como «Total estancia»
  plantilla_id: null, sales_description: "", sales_images: [],
});

export const newRoomConfig = (preset = {}) => ({
  id: uid(), room_type: "doble", pax: 2, quantity: 1, ...preset,
});
